import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileCTA from '@/components/MobileCTA';
import EnhancedQuoteCalculator from '@/components/EnhancedQuoteCalculator';
import SEO from '@/components/SEO';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calculator, Phone, MessageSquare, Truck, ClipboardList, MapPin, ChevronRight } from 'lucide-react';
import { trackPhoneClick, trackQuoteButtonClick } from '@/lib/analytics';

const QuotePage = () => {
  const { language } = useLanguage();
  const isEs = language === 'es';

  const canonicalPath = isEs ? '/es/cotizar' : '/en/quote';
  const baseUrl = 'https://thegrassdepotllc.com';

  const seoTitle = isEs
    ? 'Cotización y Calculadora de Materiales de Paisajismo | Magnolia TX'
    : 'Landscaping Material Quote & Calculator | Magnolia TX';

  const seoDescription = isEs
    ? 'Calcula al instante césped, tierra, roca, mulch y arena con entrega incluida en Magnolia, The Woodlands, Conroe, Cypress y todo el Condado de Montgomery, TX.'
    : 'Instantly calculate sod, topsoil, rock, mulch and sand with delivery included across Magnolia, The Woodlands, Conroe, Cypress and all of Montgomery County, TX.';

  const seoKeywords = isEs
    ? 'cotizacion materiales paisajismo texas, calculadora sod magnolia tx, precio entrega mulch montgomery county, calculadora granel texas'
    : 'landscaping material quote texas, sod calculator magnolia tx, mulch delivery price montgomery county, bulk material calculator texas';

  const steps = isEs
    ? [
        { title: '1. Elige tus materiales', body: 'Selecciona sod, mulch, tierra, roca o arena y agrega cada uno a tu cotización.', icon: ClipboardList },
        { title: '2. Indica tu dirección', body: 'La calculadora usa las millas hasta tu ubicación para estimar la tarifa de entrega real.', icon: MapPin },
        { title: '3. Revisa el total', body: 'Subtotal por material + entrega. Sin costos ocultos, en dólares (USD).', icon: Calculator },
        { title: '4. Confirma tu pedido', body: 'Guarda tu cotización o llámanos al (936) 320-0763 para agendar la entrega.', icon: Truck },
      ]
    : [
        { title: '1. Choose your materials', body: 'Pick sod, mulch, topsoil, rock or sand and add each item to your quote.', icon: ClipboardList },
        { title: '2. Enter your address', body: 'The calculator uses roundtrip miles to your address to estimate the real delivery fee.', icon: MapPin },
        { title: '3. Review the total', body: 'Material subtotal + delivery, priced in USD. No hidden fees.', icon: Calculator },
        { title: '4. Confirm your order', body: 'Save your quote or call (936) 320-0763 to schedule delivery.', icon: Truck },
      ];

  const benefits = isEs
    ? [
        'Precios reales por producto y unidad (palet, yarda cúbica).',
        'Costo de entrega calculado por millas de ida y vuelta desde Magnolia, TX.',
        'Puedes combinar sod y granel en la misma cotización.',
        'Guarda tu cotización creando una cuenta gratis.',
        'Atención bilingüe en inglés y español.',
      ]
    : [
        'Real per-unit pricing (pallet, cubic yard) — not estimates.',
        'Delivery cost calculated from roundtrip miles to our Magnolia, TX yard.',
        'Combine sod and bulk materials in a single quote.',
        'Save your quote by creating a free account.',
        'Bilingual support in English and Spanish.',
      ];

  const materials = isEs
    ? [
        { name: 'Césped en palet (sod)', href: '/es/pasto' },
        { name: 'Mulch y mantillo', href: '/es/mulch' },
        { name: 'Roca decorativa y grava', href: '/es/roca' },
        { name: 'Tierra vegetal y compost', href: '/es/tierra' },
        { name: 'Arena de construcción', href: '/es/arena' },
        { name: 'Ver todos los productos', href: '/es/productos' },
      ]
    : [
        { name: 'Pallet sod', href: '/en/grass' },
        { name: 'Mulch varieties', href: '/en/mulch' },
        { name: 'Decorative rock & gravel', href: '/en/rock' },
        { name: 'Topsoil & compost', href: '/en/soil' },
        { name: 'Construction sand', href: '/en/sand' },
        { name: 'Browse all products', href: '/en/products' },
      ];

  const faqs = isEs
    ? [
        { q: '¿La cotización es gratis?', a: 'Sí. La calculadora online y la cotización por teléfono son 100% gratuitas y sin compromiso.' },
        { q: '¿Cómo se calcula el costo de entrega?', a: 'Se calcula por millas de ida y vuelta desde 6920 FM 1488, Magnolia, TX 77354, y por tipo de camión (palet de césped vs. granel). Verás la tarifa exacta antes de confirmar.' },
        { q: '¿Cuántas yardas de mulch o tierra necesito?', a: 'Divide los pies cuadrados del área entre la cobertura por yarda a la profundidad deseada. Como guía, 1 yarda cúbica cubre ~100 pies² a 3 pulgadas. Si tienes dudas, llama al (936) 320-0763 y te ayudamos.' },
        { q: '¿Cuántos palets de sod necesito?', a: 'Un palet cubre aproximadamente 450 pies² de césped. Mide largo × ancho de tu jardín en pies y divide entre 450 para estimar palets.' },
        { q: '¿Pueden entregar el mismo día?', a: 'La mayoría de entregas se programan en 24-48 horas hábiles según la ruta. Confirma tu pedido temprano si necesitas una fecha específica.' },
        { q: '¿Aceptan pedidos comerciales grandes?', a: 'Sí. Trabajamos con paisajistas y contratistas. Para volúmenes grandes o proyectos recurrentes, contáctanos para precios por volumen.' },
        { q: '¿Qué áreas cubren?', a: 'Magnolia, The Woodlands, Conroe, Cypress, Spring, Tomball, Montgomery, Pinehurst y todo el Condado de Montgomery, TX. Consulta por direcciones fuera del área.' },
      ]
    : [
        { q: 'Is the quote really free?', a: 'Yes. Both the online calculator and a phone quote are 100% free with no obligation.' },
        { q: 'How is delivery cost calculated?', a: 'By roundtrip miles from 6920 FM 1488, Magnolia, TX 77354, and by truck type (sod pallet vs. bulk dump). You see the exact fee before you confirm.' },
        { q: 'How many yards of mulch or soil do I need?', a: 'Divide the square footage of your area by the coverage per yard at your target depth. As a guide, 1 cubic yard covers ~100 sq. ft. at 3 inches deep. Call (936) 320-0763 if you want help sizing it.' },
        { q: 'How many pallets of sod do I need?', a: 'One pallet covers roughly 450 sq. ft. Measure your lawn length × width in feet and divide by 450 to estimate pallets.' },
        { q: 'Can you deliver same-day?', a: 'Most deliveries are scheduled within 24-48 business hours based on the daily route. Book early if you need a specific date.' },
        { q: 'Do you accept large commercial orders?', a: 'Yes. We work with landscapers and contractors. For large volumes or recurring projects, contact us for volume pricing.' },
        { q: 'What areas do you cover?', a: 'Magnolia, The Woodlands, Conroe, Cypress, Spring, Tomball, Montgomery, Pinehurst and all of Montgomery County, TX. Ask about out-of-area addresses.' },
      ];

  const webAppSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": seoTitle,
    "description": seoDescription,
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Any",
    "url": `${baseUrl}${canonicalPath}`,
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "description": isEs ? 'Calculadora y cotización gratis' : 'Free quote calculator'
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(f => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a }
    }))
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonical={canonicalPath}
        structuredData={[webAppSchema, faqSchema]}
        language={language}
        breadcrumbs={[
          { name: isEs ? 'Inicio' : 'Home', url: `/${language}` },
          { name: isEs ? 'Cotización' : 'Quote', url: canonicalPath },
        ]}
      />
      <Header />

      <main>
      {/* Hero Section */}
      <section className="py-16 bg-gradient-natural text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">
            {isEs
              ? 'Calculadora y cotización de materiales de paisajismo en Texas'
              : 'Texas landscaping material calculator and quote'}
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            {isEs
              ? 'Calcula al instante césped, mulch, roca, tierra y arena con costo de entrega incluido desde Magnolia, TX.'
              : 'Instantly calculate sod, mulch, rock, topsoil and sand with delivery cost included from Magnolia, TX.'}
          </p>
        </div>
      </section>

      {/* Intro / How to request a quote */}
      <section className="py-14 bg-background">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
            {isEs ? '¿Cómo solicitar una cotización?' : 'How to request a quote'}
          </h2>
          <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
            {isEs
              ? 'Tienes dos formas de pedir tu cotización con The Grass Depot LLC: usar la calculadora online (recomendada para ver el total al instante) o llamarnos al (936) 320-0763. Ambas opciones son gratis y sin compromiso, e incluyen el costo de entrega desde nuestra ubicación en Magnolia, TX.'
              : 'You can request a quote from The Grass Depot LLC in two ways: use the online calculator (recommended — see your total instantly) or call (936) 320-0763. Both options are free and no-obligation and include delivery cost from our Magnolia, TX yard.'}
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {steps.map((s, i) => {
              const Icon = s.icon;
              return (
                <Card key={i} className="p-5">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{s.title}</h3>
                  <p className="text-sm text-muted-foreground">{s.body}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Calculator */}
      <EnhancedQuoteCalculator />

      {/* Benefits */}
      <section className="py-14 bg-background">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
            {isEs ? 'Por qué usar nuestra calculadora' : 'Why use our calculator'}
          </h2>
          <ul className="space-y-3">
            {benefits.map((b, i) => (
              <li key={i} className="flex items-start gap-3 text-lg text-muted-foreground">
                <ChevronRight className="h-5 w-5 text-primary flex-shrink-0 mt-1" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Materials internal links */}
      <section className="py-14 bg-gradient-subtle">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4 text-center">
            {isEs ? 'Materiales disponibles para cotizar' : 'Materials available to quote'}
          </h2>
          <p className="text-lg text-muted-foreground text-center mb-8 max-w-2xl mx-auto">
            {isEs
              ? 'Todos los productos se pueden cotizar con entrega o recogida en Magnolia, TX.'
              : 'All products below can be quoted with delivery or pickup from Magnolia, TX.'}
          </p>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            {materials.map((m) => (
              <Link
                key={m.href}
                to={m.href}
                className="flex items-center justify-between p-4 rounded-lg border bg-card hover:border-primary hover:shadow-card transition-all"
              >
                <span className="font-medium text-foreground">{m.name}</span>
                <ChevronRight className="h-4 w-4 text-primary" />
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to={isEs ? '/es/servicios' : '/en/services'}
              className="text-primary underline underline-offset-4 hover:no-underline"
            >
              {isEs ? 'Ver nuestros servicios de entrega' : 'See our delivery services'}
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {isEs ? 'Preguntas frecuentes sobre cotizaciones' : 'Quote frequently asked questions'}
            </h2>
            <p className="text-lg text-muted-foreground">
              {isEs ? 'Respuestas sobre precios, entrega y cálculo de cantidades.' : 'Answers on pricing, delivery and sizing quantities.'}
            </p>
          </div>
          <div className="space-y-4">
            {faqs.map((f, i) => (
              <Card key={i} className="p-6">
                <h3 className="text-lg font-semibold text-foreground mb-2">{f.q}</h3>
                <p className="text-muted-foreground leading-relaxed">{f.a}</p>
              </Card>
            ))}
          </div>
          <div className="mt-10 text-center flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="tel:9363200763"
              data-event-name="phone_click"
              data-source="quote_faq"
              onClick={() => trackPhoneClick('quote_faq')}
            >
              <Button variant="hero" size="lg">
                <Phone className="mr-2 h-5 w-5" />
                {isEs ? 'Llamar (936) 320-0763' : 'Call (936) 320-0763'}
              </Button>
            </a>
            <Link
              to={isEs ? '/es/contacto' : '/en/contact'}
              onClick={() => trackQuoteButtonClick('quote_faq_contact')}
            >
              <Button variant="outline" size="lg">
                <MessageSquare className="mr-2 h-5 w-5" />
                {isEs ? 'Ir a contacto' : 'Go to contact'}
              </Button>
            </Link>
          </div>
        </div>
      </section>
      </main>

      <Footer />
      <MobileCTA />
    </div>
  );
};

export default QuotePage;
